import { motion, useScroll, useTransform } from "framer-motion";
import { CtaButton } from "./shared";

const ease = [0.22, 1, 0.36, 1];

const Line = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay: 0.35 + i * 0.13, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const nodes = [
  [6, 14], [22, 6], [40, 18], [58, 8], [74, 16], [90, 10],
  [12, 55], [30, 68], [52, 58], [70, 72], [86, 52], [95, 78],
];
const edges = [[0,1],[1,2],[2,3],[3,4],[4,5],[0,6],[2,8],[6,7],[7,8],[8,9],[9,10],[10,5],[9,11],[4,10],[1,8]];

const Network = () => (
  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
    {edges.map(([a, b], i) => (
      <motion.line
        key={i}
        x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]}
        stroke="#2f6ea8" strokeOpacity="0.18" strokeWidth="0.12"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 1.6, delay: 0.4 + i * 0.07, ease: "easeOut" }}
      />
    ))}
    {nodes.map(([x, y], i) => (
      <motion.circle
        key={i} cx={x} cy={y} r={i % 3 === 0 ? 0.55 : 0.35}
        fill={["#0E5F46", "#3B7BC8", "#0B1B2B", "#E8833A"][i % 4]}
        initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 0.7, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.8 + i * 0.06 }}
        className="hero-node"
        style={{ animationDelay: `${i * 0.5}s` }}
      />
    ))}
  </svg>
);

const marqueeItems = [
  "Trapped Cash", "Five-Pillar Diagnostic", "7-Day Discovery", "90-Day Recovery",
  "CFO Validation", "$185.9M Identified", "$127.7M Realized", "68% Recovered",
  "Portfolio Parallel Diagnostics", "Fixed $25,000 Fee",
];

export const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 130]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.25]);

  return (
    <section className="relative min-h-screen flex flex-col dot-grid overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY, opacity: fade }} className="absolute inset-0 pointer-events-none">
        <Network />
      </motion.div>

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 w-full flex-1 flex flex-col justify-center pt-32 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25, ease }}
          className="inline-flex items-center gap-3 self-start bg-white rounded-full pl-5 pr-6 py-3 shadow-[0_2px_16px_rgba(11,27,43,0.08)] mb-8"
          data-testid="hero-badge"
        >
          <span className="w-2.5 h-2.5 rounded-full bg-[#0E5F46] animate-pulse" />
          <span className="text-[16px] md:text-[17px] font-bold text-[#0B1B2B]">
            Enterprise Value Intelligence™ <span className="font-semibold text-[#0B1B2B]/70">for Private Equity</span>
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold text-[#0B1B2B] tracking-[-0.03em] leading-[0.98] text-4xl sm:text-5xl lg:text-6xl xl:text-[76px] max-w-[1000px]" data-testid="hero-headline">
          <Line i={0}>Every portfolio company</Line>
          <Line i={1}>is carrying <span className="text-[#0E5F46]">trapped cash.</span></Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95, ease }}
          className="mt-8 max-w-[620px] text-[#3c4a58] text-base md:text-lg leading-relaxed"
          data-testid="hero-subtext"
        >
          Trapped Cash is working capital unnecessarily tied up by siloed policies, oversized buffers,
          and uncoordinated cross-functional decisions. It's invisible to your ops teams — and recoverable by design.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease }}
          className="mt-5 max-w-[620px] text-[#0B1B2B] text-base md:text-lg leading-relaxed font-medium"
        >
          In seven days we will find it. <span className="font-bold text-[#0E5F46]">In ninety days you will bank it.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease }}
          className="mt-8 flex flex-wrap items-center gap-5"
        >
          <CtaButton testId="hero-cta-button">Book a 20-minute Discovery call</CtaButton>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.35 }}
          className="mt-5 font-display font-bold text-[15px] md:text-base tracking-[0.03em] text-[#0B1B2B]/80"
          data-testid="hero-tagline"
        >
          Capturing Trapped Value: Cash – Margin – Throughput
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-2 text-base md:text-lg font-bold text-[#0B1B2B]/75 tracking-wide"
          data-testid="hero-trust-bar"
        >
          <span>Fixed $25,000 per company</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#0E5F46]" />
          <span>68% average recovery rate</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#0E5F46]" />
          <span>Full portfolio parallel diagnostics</span>
        </motion.div>
      </div>

      <div className="relative border-t border-[#0B1B2B]/8 bg-white/70 backdrop-blur-sm py-5 overflow-hidden" data-testid="hero-marquee">
        <div className="marquee-track flex items-center gap-10 whitespace-nowrap">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} className="flex items-center gap-10 text-[15px] font-semibold text-[#0B1B2B]/70">
              {item}
              <span className="w-1.5 h-1.5 rounded-full bg-[#0E5F46]/50 shrink-0" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
